<template>
    <div class="modal-admin-game-settings">
        <div class="game-title">
            <span class="gradient-green">{{gameName}} SETTINGS</span>
        </div>
        <div class="game-content">
            <div class="content-section" v-if="gameKey === 'crash' && !adminCrashSettings.loading">
                <!-- Time Based Config -->
                <div class="config-item config-toggle hide">
                    <div class="config-label">Time Based Config Enabled</div>
                    <button 
                        v-on:click="updateConfig('timeBasedConfigEnabled', !localConfig.timeBasedConfigEnabled)" 
                        v-bind:class="{ 'button-active': localConfig.timeBasedConfigEnabled === true }" 
                        v-bind:disabled="socketSendLoading !== null"
                        class="button-toggle"
                    ></button>
                </div>

                <!-- Instant Crash Chance -->
                <div class="config-item config-input">
                    <div class="config-label">Instant Crash Chance</div>
                    <input 
                        type="number" 
                        step="0.01"
                        v-model.number="localConfig.instantCrashChance"
                        placeholder="0.00"
                        class="config-input-field"
                    />
                </div>

                <!-- Power Curve -->
                <div class="config-item config-input">
                    <div class="config-label">Power Curve</div>
                    <input 
                        type="number" 
                        step="0.01"
                        v-model.number="localConfig.powerCurve"
                        placeholder="0.00"
                        class="config-input-field"
                    />
                </div>

                <!-- Max Multiplier -->
                <div class="config-item config-input">
                    <div class="config-label">Max Multiplier</div>
                    <input 
                        type="number" 
                        step="1"
                        v-model.number="localConfig.maxMultiplier"
                        placeholder="0"
                        class="config-input-field"
                    />
                </div>

                <!-- Peak Power Curve -->
                <div class="config-item config-input hide">
                    <div class="config-label">Peak Power Curve</div>
                    <input 
                        type="number" 
                        step="0.01"
                        v-model.number="localConfig.peakPowerCurve"
                        placeholder="0.00"
                        class="config-input-field"
                    />
                </div>

                <!-- Peak Max Multiplier -->
                <div class="config-item config-input hide">
                    <div class="config-label">Peak Max Multiplier</div>
                    <input 
                        type="number" 
                        step="1"
                        v-model.number="localConfig.peakMaxMultiplier"
                        placeholder="0"
                        class="config-input-field"
                    />
                </div>

                <!-- Offpeak Power Curve -->
                <div class="config-item config-input hide">
                    <div class="config-label">Offpeak Power Curve</div>
                    <input 
                        type="number" 
                        step="0.01"
                        v-model.number="localConfig.offpeakPowerCurve"
                        placeholder="0.00"
                        class="config-input-field"
                    />
                </div>

                <!-- Offpeak Max Multiplier -->
                <div class="config-item config-input hide">
                    <div class="config-label">Offpeak Max Multiplier</div>
                    <input 
                        type="number" 
                        step="1"
                        v-model.number="localConfig.offpeakMaxMultiplier"
                        placeholder="0"
                        class="config-input-field"
                    />
                </div>

                <!-- Weekend Power Curve -->
                <div class="config-item config-input hide">
                    <div class="config-label">Weekend Power Curve</div>
                    <input 
                        type="number" 
                        step="0.01"
                        v-model.number="localConfig.weekendPowerCurve"
                        placeholder="0.00"
                        class="config-input-field"
                    />
                </div>

                <!-- Weekend Max Multiplier -->
                <div class="config-item config-input hide">
                    <div class="config-label">Weekend Max Multiplier</div>
                    <input 
                        type="number" 
                        step="1"
                        v-model.number="localConfig.weekendMaxMultiplier"
                        placeholder="0"
                        class="config-input-field"
                    />
                </div>

                <!-- Save Button -->
                <div class="config-actions">
                    <button 
                        v-on:click="saveConfig()" 
                        class="button-save"
                        v-bind:disabled="socketSendLoading !== null"
                    >
                        <div class="button-inner">SAVE CONFIGURATION</div>
                    </button>
                </div>
            </div>

            <!-- Loading State -->
            <div class="content-section" v-if="gameKey === 'crash' && adminCrashSettings.loading">
                <div class="config-placeholder">
                    <div class="placeholder-text">Loading configuration...</div>
                </div>
            </div>

            <!-- Placeholder for other games -->
            <div class="content-section" v-else-if="gameKey !== 'crash'">
                <div class="config-placeholder">
                    <div class="placeholder-text">Configuration settings for {{gameName}} are not available yet.</div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';

export default {
    name: 'ModalAdminGameSettings',
    data() {
        return {
            localConfig: {
                timeBasedConfigEnabled: false,
                instantCrashChance: 0,
                powerCurve: 0,
                maxMultiplier: 0,
                peakPowerCurve: 0,
                peakMaxMultiplier: 0,
                offpeakPowerCurve: 0,
                offpeakMaxMultiplier: 0,
                weekendPowerCurve: 0,
                weekendMaxMultiplier: 0
            }
        }
    },
    computed: {
        ...mapGetters([
            'modalsData',
            'adminCrashSettings',
            'socketSendLoading'
        ]),
        gameKey() {
            return this.modalsData?.gameKey || null;
        },
        gameName() {
            return this.modalsData?.gameName || 'Game';
        },
        gameConfig() {
            if (this.gameKey === 'crash' && this.adminCrashSettings?.data) {
                return this.adminCrashSettings.data || {};
            }
            return {};
        }
    },
    watch: {
        gameConfig: {
            immediate: true,
            deep: true,
            handler(newConfig) {
                if (newConfig && Object.keys(newConfig).length > 0) {
                    this.localConfig = {
                        timeBasedConfigEnabled: newConfig.timeBasedConfigEnabled !== undefined ? newConfig.timeBasedConfigEnabled : false,
                        instantCrashChance: newConfig.instantCrashChance !== undefined ? parseFloat(newConfig.instantCrashChance) : 0,
                        powerCurve: newConfig.powerCurve !== undefined ? parseFloat(newConfig.powerCurve) : 0,
                        maxMultiplier: newConfig.maxMultiplier !== undefined ? parseInt(newConfig.maxMultiplier) : 0,
                        peakPowerCurve: newConfig.peakPowerCurve !== undefined ? parseFloat(newConfig.peakPowerCurve) : 0,
                        peakMaxMultiplier: newConfig.peakMaxMultiplier !== undefined ? parseInt(newConfig.peakMaxMultiplier) : 0,
                        offpeakPowerCurve: newConfig.offpeakPowerCurve !== undefined ? parseFloat(newConfig.offpeakPowerCurve) : 0,
                        offpeakMaxMultiplier: newConfig.offpeakMaxMultiplier !== undefined ? parseInt(newConfig.offpeakMaxMultiplier) : 0,
                        weekendPowerCurve: newConfig.weekendPowerCurve !== undefined ? parseFloat(newConfig.weekendPowerCurve) : 0,
                        weekendMaxMultiplier: newConfig.weekendMaxMultiplier !== undefined ? parseInt(newConfig.weekendMaxMultiplier) : 0
                    };
                }
            }
        }
    },
    methods: {
        ...mapActions([
            'adminGetCrashConfigSocket',
            'adminSetCrashConfigSocket',
            'notificationShow'
        ]),
        updateConfig(key, value) {
            // Update local config immediately for UI responsiveness
            this.localConfig[key] = value;
        },
        saveConfig() {
            if (this.gameKey !== 'crash') return;

            // Prepare config data
            const configData = { ...this.localConfig };
            
            // Send to backend - the store action will close the modal on success
            this.adminSetCrashConfigSocket(configData);
        }
    },
    mounted() {
        // Fetch crash config when modal opens
        if (this.gameKey === 'crash') {
            this.adminGetCrashConfigSocket();
        }
    }
}
</script>

<style scoped>
.hide {
    display: none !important;
}
.modal-admin-game-settings {
    width: 720px;
    max-width: 90vw;
    position: relative;
    display: flex;
    flex-direction: column;
    padding: 35px 30px 30px 30px;
    border-radius: 15px;
    background: radial-gradient(100% 100% at 50% 0%, rgba(0, 255, 194, 0.25) 0%, rgba(7, 38, 61, 0) 80%), 
                linear-gradient(256deg, #07263d 0%, #07243a 100%);
}

.modal-admin-game-settings .game-title {
    text-align: center;
    font-size: 24px;
    font-weight: 800;
    margin-bottom: 25px;
}

.modal-admin-game-settings .game-content {
    width: 100%;
}

.modal-admin-game-settings .content-section {
    width: 100%;
}

.modal-admin-game-settings .section-title {
    font-size: 18px;
    font-weight: 700;
    color: #ffffff;
    margin-bottom: 10px;
}

.modal-admin-game-settings .section-info {
    font-size: 14px;
    font-weight: 500;
    color: #8bacc8;
    margin-bottom: 25px;
    line-height: 1.6;
}

.modal-admin-game-settings .section-info strong {
    color: #ffffff;
    font-weight: 700;
}

.modal-admin-game-settings .config-item {
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 15px;
    margin-bottom: 10px;
    border-radius: 8px;
    background: rgba(15, 23, 42, 0.6);
    border: 1px solid rgba(212, 165, 116, 0.2);
}

.modal-admin-game-settings .config-label {
    font-size: 14px;
    font-weight: 600;
    color: #bbbfd0;
    flex: 1;
}

.modal-admin-game-settings .config-input-field {
    width: 150px;
    padding: 8px 12px;
    border: 1px solid rgba(212, 165, 116, 0.3);
    border-radius: 6px;
    background: rgba(0, 0, 0, 0.3);
    color: #ffffff;
    font-size: 14px;
    font-weight: 600;
    outline: none;
    transition: border-color 0.3s ease;
}

.modal-admin-game-settings .config-input-field:focus {
    border-color: rgba(212, 165, 116, 0.6);
}

.modal-admin-game-settings .config-input-field::placeholder {
    color: #6b7c8a;
}

.modal-admin-game-settings .button-toggle {
    width: 45px;
    height: 15px;
    position: relative;
    border: none;
    background: transparent;
    cursor: pointer;
    filter: drop-shadow(0px 4px 25px rgba(15, 41, 63, 0.35));
}

.modal-admin-game-settings .button-toggle:disabled {
    cursor: not-allowed;
    opacity: 0.5;
}

.modal-admin-game-settings .button-toggle::before {
    content: '';
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
    background-color: var(--bg-blue-chat);
    clip-path: polygon(4px 0, calc(100% - 4px) 0, 100% 25%, 100% 75%, calc(100% - 4px) 100%, 4px 100%, 0 75%, 0 25%);
}

.modal-admin-game-settings .button-toggle::after {
    content: '';
    width: 25px;
    height: 19px;
    position: absolute;
    top: -2px;
    left: 0;
    background: var(--accent-blue-light);
    clip-path: polygon(3px 0, calc(100% - 3px) 0, 100% 25%, 100% 75%, calc(100% - 3px) 100%, 3px 100%, 0 75%, 0 25%);
    transition: all 0.3s ease;
}

.modal-admin-game-settings .button-toggle.button-active::after {
    transform: translate(20px, 0);
    background: var(--gradient-button-bg);
}

.modal-admin-game-settings .config-actions {
    width: 100%;
    margin-top: 20px;
    display: flex;
    justify-content: center;
}

.modal-admin-game-settings .button-save {
    padding: 12px 30px;
    border: none;
    border-radius: 8px;
    background: var(--gradient-button-bg);
    cursor: pointer;
    transition: all 0.3s ease;
}

.modal-admin-game-settings .button-save:hover:not(:disabled) {
    transform: translateY(-1px);
    filter: brightness(1.1);
}

.modal-admin-game-settings .button-save:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.modal-admin-game-settings .button-save .button-inner {
    font-size: 14px;
    font-weight: 700;
    color: #ffffff;
    letter-spacing: 0.5px;
    text-transform: uppercase;
}

.modal-admin-game-settings .config-placeholder {
    width: 100%;
    padding: 40px 20px;
    text-align: center;
}

.modal-admin-game-settings .placeholder-text {
    font-size: 14px;
    font-weight: 500;
    color: #8bacc8;
}

@media only screen and (max-width: 768px) {
    .modal-admin-game-settings {
        width: 95vw;
        padding: 25px 20px 20px 20px;
    }

    .modal-admin-game-settings .game-title {
        font-size: 20px;
        margin-bottom: 20px;
    }
}
</style>

